/**
 * useApiFetch — la LECTURE à un coup des ports (patron B), en un seul endroit.
 *
 * L'idiome est celui de `ports/CLAUDE.md` § « Consuming a one-shot API call », écrit une fois :
 * une garde `awaiting` (« est-ce NOUS qui avons demandé ? » — une autre surface peut nourrir la
 * même tranche), une garde par issue, la tranche rendue au repos après le succès et au démontage,
 * et une attente qui se lit sur la CHARGE locale, jamais sur `request` : la tranche est mise au
 * repos avant que le rendu suivant n'applique la charge, donc « terminé, sans rien » se lirait
 * comme une erreur vide.
 *
 * L'ERREUR, elle, reste sur la tranche : c'est le view-model qui y lit la raison
 * (`activationLandingViewModel` distingue « lien invalide » d'une panne, `cancelFeesViewModel` et
 * `referralDashboardViewModel` en font le message). `idleOnError` est réservé aux tranches que personne
 * d'autre ne lit ; le crochet garde alors l'échec qu'il a reçu (`error`, et une attente qui s'arrête),
 * jusqu'à la lecture suivante — la tranche remise à zéro ne rallume jamais l'attente.
 *
 * Une tranche que plusieurs surfaces lisent pour des CLÉS différentes (les créneaux d'un
 * rattrapage et ceux d'un déplacement de séance, sur `getRecoveryPossibilitiesAsParent`) se lit
 * avec `scopeUrlParams` et `fetchWith(urlParams)` : la tranche renvoie les `urlParams` de l'appel
 * qui l'a remplie, et une réponse n'est consommée que si chacune de ces clés vaut ce qu'on a
 * envoyé. `cancel()` coupe une lecture dont l'écran ne veut plus (la feuille se ferme).
 *
 * Un appelant ne garde que ses effets PROPRES (Amplitude, balise de lecture, préchargement).
 * Référence de l'idiome écrit à la main : `useLiveReportDetailsData`.
 */
import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiIdle, apiRequest } from "../../../../data/redux/actions/api"

/** Ce que `apiRequest` retrouve dans la tranche : une fonction de service nommée. */
export type ApiService = { name: string }

export type ApiFetchSlice<T> = {
    request?: boolean
    success?: boolean
    error?: unknown
    data?: T
    urlParams?: Record<string, unknown>
}

export type UseApiFetchOptions<T> = {
    service: ApiService
    routeParams?: Record<string, unknown>
    urlParams?: Record<string, unknown>
    /** L'IDENTITÉ de la demande : elle change ⇒ on redemande. Les objets, eux, changent à chaque rendu. */
    requestKey?: string | null
    /** `false` : il n'y a rien à demander (pas de lien signé, pas de hash) — le crochet reste inerte. */
    enabled?: boolean
    /** `false` : seul un geste déclenche l'appel. */
    fetchOnMount?: boolean
    /** Le geste ne relance pas un appel déjà en vol — `fetch()` rend alors `false`. */
    skipWhilePending?: boolean
    /** Tranche que personne d'autre ne lit : on la rend au repos aussi après une erreur traitée. */
    idleOnError?: boolean
    /** Services à rendre au repos au démontage (et au changement de demande). Défaut : celui qu'on appelle. */
    idleOnUnmount?: ApiService[]
    /**
     * Les paramètres d'URL qui disent POUR QUI la lecture est partie. Une réponse n'est lue que si
     * chacune de ces clés vaut ce qu'on a envoyé ; sans `urlParams`, elle n'est pas la nôtre.
     */
    scopeUrlParams?: string[]
    onSuccess?: (data: T | null) => void
    onError?: (error: unknown) => void
}

export function useApiFetch<T>(options: UseApiFetchOptions<T>) {
    const { service, requestKey = null, enabled = true, fetchOnMount = true } = options
    const dispatch = useDispatch()
    const [raw, setRaw] = useState<T | null>(null)
    /** La réponse est arrivée — `null` compris : « rien à montrer » est une réponse, pas une attente. */
    const [answered, setAnswered] = useState(false)
    /** L'échec reçu par notre lecture, gardé ici : une tranche rendue au repos (`idleOnError`) l'a oublié. */
    const [failure, setFailure] = useState<unknown>(null)

    /** Les options au dernier rendu : les effets les lisent sans se redéclarer à chaque rappel. */
    const optionsRef = useRef(options)
    optionsRef.current = options

    const req = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => state?.api?.[service.name] as ApiFetchSlice<T> | undefined
    )

    const awaitingRef = useRef(false)
    const handledSuccessRef = useRef(false)
    const handledErrorRef = useRef(false)
    /** Une lecture coupée : sa réponse, quand elle arrive, est rendue au repos sans être lue. */
    const discardingRef = useRef(false)
    /** Les `urlParams` de NOTRE dernier appel — ce que `scopeUrlParams` compare à la tranche. */
    const sentUrlParamsRef = useRef<Record<string, unknown>>({})

    const request = useCallback(
        (urlParams: Record<string, unknown>): boolean => {
            const current = optionsRef.current
            if (current.enabled === false) return false
            if (current.skipWhilePending && awaitingRef.current) return false
            awaitingRef.current = true
            handledSuccessRef.current = false
            handledErrorRef.current = false
            discardingRef.current = false
            sentUrlParamsRef.current = urlParams
            setRaw(null)
            setAnswered(false)
            setFailure(null)
            dispatch(apiRequest(current.service, current.routeParams ?? {}, {}, urlParams) as never)
            return true
        },
        [dispatch]
    )

    /** Rend `false` quand rien n'a été demandé — le geste qui accompagne l'appel sait se retenir. */
    const fetch = useCallback(
        (): boolean => request(optionsRef.current.urlParams ?? {}),
        // `requestKey` résume les paramètres : les caler sur les objets relancerait à chaque rendu.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [request, service, requestKey, enabled]
    )

    /** La même lecture, pour la clé du moment : c'est elle que `scopeUrlParams` reconnaîtra. */
    const fetchWith = useCallback(
        (urlParams: Record<string, unknown>): boolean => request(urlParams),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [request, service]
    )

    /** Coupe l'attente et rend la tranche au repos — maintenant, et quand la réponse arrivera. */
    const cancel = useCallback(() => {
        if (!awaitingRef.current) return
        awaitingRef.current = false
        discardingRef.current = true
        dispatch(apiIdle(optionsRef.current.service) as never)
    }, [dispatch])

    /** La tranche renvoie les `urlParams` de l'appel qui l'a remplie : on ne lit que la nôtre. */
    const isOurResponse = (slice: ApiFetchSlice<T> | undefined): boolean => {
        const scope = optionsRef.current.scopeUrlParams
        if (!scope?.length) return true
        if (slice?.urlParams == null) return false
        const sent = sentUrlParamsRef.current
        return scope.every((key) => String(slice.urlParams?.[key] ?? "") === String(sent[key] ?? ""))
    }

    /** La réponse d'une lecture coupée : personne ne la lira, elle ne doit pas rester sur la tranche. */
    const discardIfCancelled = (): boolean => {
        if (!discardingRef.current || !isOurResponse(req)) return false
        discardingRef.current = false
        dispatch(apiIdle(optionsRef.current.service) as never)
        return true
    }

    useEffect(() => {
        if (!fetchOnMount) return
        fetch()
    }, [fetch, fetchOnMount])

    useEffect(() => {
        if (!req?.success || discardIfCancelled()) return
        if (!awaitingRef.current || handledSuccessRef.current || !isOurResponse(req)) return
        handledSuccessRef.current = true
        awaitingRef.current = false
        const data = req.data ?? null
        setRaw(data)
        setAnswered(true)
        optionsRef.current.onSuccess?.(data)
        dispatch(apiIdle(optionsRef.current.service) as never)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, req?.success, req?.data, req?.urlParams])

    useEffect(() => {
        if (!req?.error || discardIfCancelled()) return
        if (!awaitingRef.current || handledErrorRef.current || !isOurResponse(req)) return
        handledErrorRef.current = true
        awaitingRef.current = false
        setFailure(req.error)
        optionsRef.current.onError?.(req.error)
        if (optionsRef.current.idleOnError) dispatch(apiIdle(optionsRef.current.service) as never)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, req?.error, req?.urlParams])

    // Une erreur laissée sur la tranche se rejouerait au prochain montage de l'écran.
    useEffect(
        () => () => {
            const current = optionsRef.current
            for (const idled of current.idleOnUnmount ?? [current.service]) {
                dispatch(apiIdle(idled) as never)
            }
        },
        [dispatch, requestKey]
    )

    return {
        raw,
        error: req?.error ?? failure ?? null,
        loading: enabled && !answered && req?.error == null && failure == null,
        fetch,
        fetchWith,
        cancel,
    }
}
